import type { Mode } from '@connected/ui/store/theme';

import { useAuthentication } from '@connected/api-client/hooks/use-authentication';
import { removeAuthentication } from '@connected/api-client/lib/authentication';
import { getLogoutRedirectUrl } from '@connected/api-client/lib/portal';
import { generateSSOUrl } from '@connected/api-client/lib/sso';
import { ElectricalProjectProvider } from '@connected/elektra-sdk/components/providers/project';
import { useElectricalProject } from '@connected/elektra-sdk/hooks/use-project';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { Label } from '@connected/form-fields/components/label';
import { Button } from '@connected/ui/components/button';
import { Dialog } from '@connected/ui/components/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@connected/ui/components/popover';
import { QRCodeSVG } from '@connected/ui/components/qr-code-svg';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@connected/ui/components/select';
import { Separator } from '@connected/ui/components/separator';
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarInset, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger } from '@connected/ui/components/sidebar';
import { Switch } from '@connected/ui/components/switch';
import { useScreenSize } from '@connected/ui/hooks/use-screen-size';
import { useSidebar } from '@connected/ui/hooks/use-sidebar';
import { useTheme } from '@connected/ui/hooks/use-theme';
import { onlineManager, useQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router';
import { ArrowUpRight, Folder, LogOut, QrCode, Settings, UserCircle2, UserRound } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { AppVersion } from '@/components/app-version';
import { ProjectStatusMenuItem } from '@/components/features/project-status';
import { Loading } from '@/components/loading';
import { NavigationGroups } from '@/components/navigation/navigation-groups';
import { ElectricalProjectFilesProvider } from '@/components/providers/electrical-project-files';
import { useConditionContext } from '@/config/conditions';
import { NavigationProvider } from '@/config/navigation-config';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useLegacy } from '@/hooks/use-legacy';
import { usePrefetch } from '@/hooks/use-prefetch';
import { queryClient } from '@/lib/query-client';
import { ElektraConditionProvider } from '@/providers/condition-provider';
import { setConnectionStatus } from '@/stores/network-status';

export const Route = createFileRoute('/(auth)/projects/$projectVersionId')({
    validateSearch: z.object({
        organizationId: z.string().optional(),
    }),
    component: Layout,
    pendingComponent: () => <Loading action="Loading project" />,
});

function Layout() {
    const { projectVersionId } = Route.useParams();

    if (!onlineManager.isOnline()) {
        setConnectionStatus('offline');
    }

    return (
        <ElectricalProjectFilesProvider electricalProjectVersionId={projectVersionId} fallback={<Loading action="Loading project files" />}>
            <ElectricalProjectProvider electricalProjectVersionId={projectVersionId} fallback={<Loading action="Loading project" />}>
                <ElektraConditionProvider>
                    <ProjectLayout />
                </ElektraConditionProvider>
            </ElectricalProjectProvider>
        </ElectricalProjectFilesProvider>
    );
}

function ProjectLayout() {
    // useConditionContext must be called inside ElektraConditionProvider
    const navigationContext = useConditionContext();

    return (
        <NavigationProvider value={navigationContext}>
            <SidebarProvider>
                <Navigation />
                <Prefetcher />
                <FixedSidebarTrigger />
                <SidebarInset className="min-w-0 overflow-x-auto">
                    <Outlet />
                </SidebarInset>
            </SidebarProvider>
        </NavigationProvider>
    );
}

/**
 * Fixed position sidebar trigger that shows on mobile when sidebar is hidden
 */
function FixedSidebarTrigger() {
    const { isMobile } = useScreenSize();
    const { openMobile } = useSidebar();

    // Only show on mobile when sidebar is not open
    if (!isMobile || openMobile) {
        return null;
    }

    return (
        <SidebarTrigger
            className="fixed top-2.5 left-2 z-30 h-10 w-10 bg-background-primary border border-border shadow-md"
        />
    );
}

function Prefetcher() {
    const shouldPrefetch = useFeatureFlag({ flag: 'prefetch-project-data' });
    const { context: { electricalProjectVersionId } } = useElectricalProject();

    const { projectVersionId } = Route.useParams();

    const { prefetchData, prefetchFiles } = usePrefetch({
        queryClient,
        electricalProjectVersionId: projectVersionId,
    });

    // Refs to track prefetch execution state
    const prefetchDataExecutedRef = useRef(false);
    const prefetchFilesExecutedRef = useRef(false);
    const lastPrefetchedVersionIdRef = useRef<string | null>(null);

    // Reset refs when electricalProjectVersionId changes
    useEffect(() => {
        if (lastPrefetchedVersionIdRef.current !== electricalProjectVersionId) {
            prefetchDataExecutedRef.current = false;
            prefetchFilesExecutedRef.current = false;
            lastPrefetchedVersionIdRef.current = electricalProjectVersionId;
        }
    }, [electricalProjectVersionId]);

    useEffect(() => {
        if (shouldPrefetch && electricalProjectVersionId && !prefetchDataExecutedRef.current) {
            prefetchDataExecutedRef.current = true;
            prefetchData();
        }
    }, [shouldPrefetch, electricalProjectVersionId, prefetchData]);

    useEffect(() => {
        if (shouldPrefetch && electricalProjectVersionId && !prefetchFilesExecutedRef.current) {
            prefetchFilesExecutedRef.current = true;
            prefetchFiles();
        }
    }, [shouldPrefetch, electricalProjectVersionId, prefetchFiles]);

    return null;
}

function Navigation() {
    const { open, toggleSidebar } = useSidebar();

    return (
        <Sidebar variant="sidebar" collapsible="icon">
            <SidebarHeader className="flex h-20 flex-row items-center px-6">
                <SidebarMenu>
                    <SidebarMenuItem>
                        <button
                            type="button"
                            className="peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md px-1 py-2 text-left text-sm outline-none ring-sidebar-ring transition-[width,height,padding] hover:bg-background-primary hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-background-primary active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-background-primary data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-background-primary data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:!size-8 [&>span:last-child]:truncate"
                            onClick={() => toggleSidebar()}
                        >
                            <div className="flex h-full items-center ">
                                <svg width="23" height="33" viewBox="0 0 23 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g clipPath="url(#clip0_17510_38913)">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M16.2276 8.93912C16.761 9.2871 17.2545 9.68932 17.6993 10.1435C19.3001 11.7638 20.2893 14.0067 20.2893 16.4834C20.2893 18.9602 19.3001 21.2031 17.6993 22.8234C16.1014 24.4465 13.8896 25.4497 11.4468 25.4497C11.27 25.4497 11.093 25.4443 10.9187 25.436L18.8361 13.0142L15.0128 13.0059L16.2273 8.93855L16.2276 8.93912ZM9.29969 27.9758C9.99673 28.1091 10.7126 28.177 11.4474 28.177C14.6325 28.177 17.5147 26.8693 19.6007 24.7514C21.6893 22.6364 22.9789 19.7138 22.9789 16.4837C22.9789 13.2537 21.6893 10.3311 19.6007 8.21603C18.842 7.44384 17.9786 6.78041 17.0321 6.2503L18.898 0L16.0615 5.76358L14.8577 8.20775L11.1926 15.6462L14.8442 15.687L10.4607 25.3957L9.29688 27.9758H9.29941H9.29969Z" fill="#EE702C" />
                                        <path fillRule="evenodd" clipRule="evenodd" d="M6.75108 24.0471C6.2176 23.6991 5.7241 23.2941 5.27902 22.8427C3.67831 21.2196 2.68906 18.9767 2.68906 16.4999C2.68906 14.0232 3.67831 11.7829 5.27902 10.16C6.87973 8.53684 9.0888 7.53371 11.5315 7.53371C11.7086 7.53371 11.8854 7.53913 12.0597 7.54998L4.14225 19.9692L7.96808 19.9775L6.75079 24.0474L6.75108 24.0471ZM13.6792 5.0079C12.9847 4.87458 12.2663 4.80664 11.5315 4.80664C8.34644 4.80664 5.46426 6.11437 3.37821 8.23225C1.28963 10.3473 0 13.2699 0 16.4999C0 19.73 1.28963 22.6523 3.37821 24.7705C4.1369 25.5398 5.00285 26.2033 5.94931 26.7334L4.0806 32.9836L6.91999 27.2227L8.12376 24.7785L11.786 17.3372L8.13445 17.2964L12.5182 7.58766L13.6846 5.01046C13.6818 5.01046 13.6818 5.0079 13.6792 5.0079Z" fill="#EE702C" />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_17510_38913">
                                            <rect width="23" height="33" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>

                            </div>
                            <div className="flex h-full items-center ">
                                <svg width="84" height="15" viewBox="0 -2 84 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M0 11.1116V0.324219H7.40618V2.22787H2.32285V4.75021H6.39637V6.62213H2.32285V9.20792H7.40618V11.1116H0Z" fill="#EE702C" />
                                    <path d="M9.21875 11.1046V0H11.4744V11.1046H9.21875Z" fill="#EE702C" />
                                    <path d="M15.931 9.09186C16.1947 9.30878 16.5731 9.41725 17.0674 9.41725C18.1111 9.41725 19.0423 9.38552 19.8615 9.32207L20.3328 9.27447L20.3664 10.845C19.0759 11.1409 17.909 11.2892 16.8652 11.2892C15.5969 11.2892 14.6771 10.9719 14.1047 10.3373C13.5323 9.70279 13.2461 8.67165 13.2461 7.24391C13.2461 4.39914 14.486 2.97656 16.9659 2.97656C19.4458 2.97656 20.6352 4.17188 20.6352 6.56177L20.4671 8.0371H15.5183C15.5294 8.52371 15.667 8.8753 15.9307 9.09222L15.931 9.09186ZM18.4139 6.41863C18.4139 5.78408 18.3069 5.34248 18.0941 5.09419C17.8809 4.8459 17.5077 4.72158 16.9748 4.72158C16.4418 4.72158 16.0657 4.85107 15.847 5.11005C15.6282 5.36941 15.5128 5.80548 15.5017 6.419H18.4139V6.41863Z" fill="#EE702C" />
                                    <path d="M24.4978 11.1046H22.2422V0H24.4978V6.31377L25.3564 6.171L27.0227 3.17275H29.5476L27.3594 6.93246L29.6656 11.1046H27.1238L25.4236 8.0112L24.4978 8.15397V11.1046Z" fill="#EE702C" />
                                    <path d="M35.5655 4.98619H33.495V8.23826C33.495 8.55553 33.5034 8.78021 33.52 8.91228C33.537 9.04473 33.5982 9.15835 33.7052 9.25354C33.8118 9.34872 33.977 9.39631 34.2016 9.39631L35.481 9.36458L35.5821 11.062C34.8301 11.2206 34.2577 11.3 33.8652 11.3C32.8554 11.3 32.165 11.086 31.7947 10.6573C31.4244 10.229 31.2393 9.43837 31.2393 8.28585V4.98619H30.2461V3.17772H31.2393V0.972656H33.495V3.17772H35.5655V4.98619Z" fill="#EE702C" />
                                    <path d="M37.0433 11.0988V3.16693H39.282V4.00771C40.2247 3.47904 41.1224 3.1352 41.9751 2.97656V5.11817C41.066 5.2982 40.2859 5.48304 39.6353 5.6734L39.2986 5.78445V11.0988H37.043H37.0433Z" fill="#EE702C" />
                                    <path d="M49.7526 5.64167V8.94133C49.7751 9.19516 49.8201 9.37224 49.8872 9.47259C49.9547 9.5733 50.0949 9.64451 50.308 9.68693L50.2406 11.2892C49.6682 11.2892 49.2108 11.2519 48.8686 11.1781C48.5263 11.104 48.1811 10.956 47.8333 10.7339C47.0253 11.104 46.2006 11.2892 45.359 11.2892C43.6193 11.2892 42.75 10.4222 42.75 8.68751C42.75 7.84157 42.9912 7.24133 43.4736 6.88679C43.956 6.53263 44.6966 6.32345 45.6957 6.26036L47.4966 6.13345V5.64167C47.4966 5.31407 47.418 5.08902 47.2609 4.96765C47.1038 4.84627 46.8512 4.78503 46.5034 4.78503L43.339 4.91194L43.2715 3.43661C44.472 3.13004 45.6138 2.97656 46.697 2.97656C47.7802 2.97656 48.5595 3.18832 49.0368 3.61111C49.5136 4.03427 49.7522 4.71125 49.7522 5.64167H49.7526ZM45.9653 7.73569C45.3368 7.78881 45.0226 8.10609 45.0226 8.68751C45.0226 9.26894 45.2974 9.56002 45.8473 9.56002C46.2736 9.56002 46.7394 9.49657 47.2443 9.36966L47.497 9.3062V7.60878L45.9653 7.73569Z" fill="#EE702C" />
                                    <path d="M51.8047 7.84554V5.91016H56.787V7.84554H51.8047Z" fill="#EE702C" />
                                    <path d="M65.2096 5.64167V8.94133C65.2321 9.19516 65.2771 9.37224 65.3443 9.47259C65.4118 9.5733 65.5519 9.64451 65.7651 9.68693L65.6976 11.2892C65.1252 11.2892 64.6679 11.2519 64.3256 11.1781C63.9833 11.104 63.6381 10.956 63.2904 10.7339C62.4823 11.104 61.6576 11.2892 60.816 11.2892C59.0763 11.2892 58.207 10.4222 58.207 8.68751C58.207 7.84157 58.4482 7.24133 58.9306 6.88679C59.413 6.53263 60.1536 6.32345 61.1527 6.26036L62.9536 6.13345V5.64167C62.9536 5.31407 62.8751 5.08902 62.718 4.96765C62.5608 4.84627 62.3082 4.78503 61.9604 4.78503L58.796 4.91194L58.7285 3.43661C59.929 3.13004 61.0709 2.97656 62.154 2.97656C63.2372 2.97656 64.0165 3.18832 64.4938 3.61111C64.9707 4.03427 65.2093 4.71125 65.2093 5.64167H65.2096ZM61.4223 7.73569C60.7939 7.78881 60.4796 8.10609 60.4796 8.68751C60.4796 9.26894 60.7544 9.56002 61.3043 9.56002C61.7307 9.56002 62.1965 9.49657 62.7014 9.36966L62.954 9.3062V7.60878L61.4223 7.73569Z" fill="#EE702C" />
                                    <path d="M67.3828 14.4302V3.16693H69.6215V3.59525C70.351 3.18279 70.9905 2.97656 71.5404 2.97656C72.6738 2.97656 73.5099 3.29642 74.0483 3.9365C74.5868 4.57659 74.8564 5.66307 74.8564 7.19669C74.8564 8.73031 74.5588 9.79576 73.9643 10.3934C73.3694 10.9911 72.3931 11.2895 71.0355 11.2895C70.6652 11.2895 70.2669 11.2578 69.8406 11.1944L69.6385 11.1626V14.4306H67.3828V14.4302ZM71.103 4.88021C70.6877 4.88021 70.2669 4.95953 69.8406 5.11817L69.6385 5.19749V9.35379C70.1434 9.41724 70.5527 9.44897 70.8673 9.44897C71.5179 9.44897 71.9642 9.26931 72.2054 8.90961C72.4466 8.55027 72.5672 7.93675 72.5672 7.06941C72.5672 5.60995 72.0789 4.88021 71.1026 4.88021H71.103Z" fill="#EE702C" />
                                    <path d="M76.5273 14.4302V3.16693H78.766V3.59525C79.4955 3.18279 80.1351 2.97656 80.685 2.97656C81.8183 2.97656 82.6544 3.29642 83.1929 3.9365C83.7313 4.57659 84.0009 5.66307 84.0009 7.19669C84.0009 8.73031 83.7033 9.79576 83.1088 10.3934C82.5139 10.9911 81.5376 11.2895 80.18 11.2895C79.8098 11.2895 79.4114 11.2578 78.9851 11.1944L78.783 11.1626V14.4306H76.5273V14.4302ZM80.2472 4.88021C79.8319 4.88021 79.4111 4.95953 78.9847 5.11817L78.7826 5.19749V9.35379C79.2875 9.41724 79.6969 9.44897 80.0115 9.44897C80.6621 9.44897 81.1084 9.26931 81.3496 8.90961C81.5908 8.55027 81.7114 7.93675 81.7114 7.06941C81.7114 5.60995 81.223 4.88021 80.2468 4.88021H80.2472Z" fill="#EE702C" />
                                </svg>

                            </div>
                        </button>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarHeader>
            <SidebarContent>
                <NavigationGroups />
            </SidebarContent>
            <SidebarFooter>
                {open && (
                    <SidebarMenu className="px-4 py-2">
                        <SidebarMenuItem>
                            <AppVersion className="truncate text-nowrap whitespace-nowrap nowrap" />
                        </SidebarMenuItem>
                    </SidebarMenu>
                )}
                <Separator />
                <SidebarMenu>
                    <SidebarMenuItem>
                        <UserMenu />
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarFooter>
        </Sidebar>
    );
}

function UserMenu() {
    const auth = useAuthentication();
    const params = Route.useParams();
    const location = useLocation();
    const { t } = useTranslation('projects');
    const { t: c } = useTranslation('common');

    const { mode, setMode } = useTheme();
    const { legacy, toggleLegacy } = useLegacy();

    const qrCodeValue = useMemo(() => generateSSOUrl({ targetDomain: 'elektra-app', redirectPath: location.pathname + location.searchStr }), [location.pathname, location.searchStr]);

    const { data } = useQuery(elektraAppQueryOptions.project.edit({ electricalProjectVersionId: params.projectVersionId }));

    const handleLogout = () => {
        removeAuthentication();

        queryClient.invalidateQueries();

        window.location.href = getLogoutRedirectUrl('elektra-app');
    };

    function handleQrCodeCopy() {
        if (qrCodeValue) {
            navigator.clipboard.writeText(qrCodeValue);
            toast.success(t('messages.toast.qrCodeCopied', { ns: 'common' }));
        }
    }

    if (auth.isAuthenticated !== true)
        return null;

    return (
        <Popover>
            <PopoverTrigger asChild>
                <SidebarMenuButton className="w-full px-4 py-6 ms-4">
                    <UserRound size={16} />
                    <span className="truncate">
                        {auth.currentUser?.DisplayName}
                    </span>
                </SidebarMenuButton>
            </PopoverTrigger>
            <PopoverContent className="p-2" side="right" align="end">
                <div className="flex flex-col gap-2">
                    <Button variant="ghost" className="justify-start gap-2" asChild>
                        <a href={generateSSOUrl({ targetDomain: 'portaal', redirectPath: '/customer' }) ?? '#'}>
                            <ArrowUpRight size={24} />
                            <span>{t('userMenu.portal')}</span>
                        </a>
                    </Button>
                    <Button variant="ghost" className="justify-start gap-2" asChild>
                        <a href={generateSSOUrl({ targetDomain: 'portaal', redirectPath: `/dashboard/organizations/${data?.Project?.OrganizationId}` }) ?? '#'}>
                            <ArrowUpRight size={24} />
                            <span>{t('userMenu.dashboard')}</span>
                        </a>
                    </Button>
                    <Separator />
                    <Button variant="ghost" className="justify-start gap-2" asChild>
                        <a href={generateSSOUrl({ targetDomain: 'portaal', redirectPath: `/dashboard/organizations/${data?.Project?.OrganizationId}/projects/${params.projectVersionId}/documents` }) ?? '#'}>
                            <Folder size={24} />
                            <span>{t('userMenu.documents')}</span>
                        </a>
                    </Button>
                    <ProjectStatusMenuItem />
                    <Separator />
                    {!legacy && (
                        <Button variant="ghost" className="justify-start gap-2" asChild>
                            <a href={generateSSOUrl({ targetDomain: 'portaal', redirectPath: `/dashboard/account/${auth.currentUser?.UserId}/profile` }) ?? '#'}>
                                <UserCircle2 size={24} />
                                <span>{t('userMenu.profile')}</span>
                            </a>
                        </Button>
                    )}
                    <Button variant="ghost" className="justify-start gap-2" asChild>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="ghost"
                                    className="justify-start gap-2"
                                >
                                    <Settings size={24} />
                                    <span>{t('userMenu.settings')}</span>
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="p-2" side="right">
                                <Label>
                                    <span className="p-2 pb-3">{t('userMenu.theme')}</span>
                                    <Select value={mode} onValueChange={value => setMode(value as Mode)}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder={t('userMenu.selectTheme')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="light">{c('actions.themes.light')}</SelectItem>
                                            <SelectItem value="dark">{c('actions.themes.dark')}</SelectItem>
                                            <SelectItem value="system">{c('actions.themes.system')}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </Label>
                                <Label className="flex flex-col gap-2 p-2">
                                    <span>{t('userMenu.legacyMode')}</span>
                                    <Switch checked={legacy} onCheckedChange={toggleLegacy} />
                                </Label>
                            </PopoverContent>
                        </Popover>
                    </Button>
                    {auth.currentUser.IsAdmin && (
                        <Dialog>
                            <Dialog.Trigger asChild>
                                <Button variant="ghost" className="justify-start gap-2">
                                    <QrCode size={24} />
                                    <span>{t('userMenu.loginOtherDevice')}</span>
                                </Button>
                            </Dialog.Trigger>
                            <Dialog.Content>
                                <Dialog.Header>
                                    <Dialog.Title>{t('userMenu.loginOtherDevice')}</Dialog.Title>
                                    <Dialog.Description>{t('userMenu.scanQrCode')}</Dialog.Description>
                                </Dialog.Header>
                                <QRCodeSVG className="w-full h-full" value={qrCodeValue} />
                                <Dialog.Footer>
                                    {auth.currentUser.IsAdmin && (
                                        <Button variant="outline" className="w-full gap-2" onClick={handleQrCodeCopy} disabled={!qrCodeValue}>
                                            <span>{t('userMenu.copyQrCode')}</span>
                                        </Button>
                                    )}
                                    <Dialog.Close asChild>
                                        <Button variant="outline" className="w-full">{c('actions.general.close')}</Button>
                                    </Dialog.Close>
                                </Dialog.Footer>
                            </Dialog.Content>
                        </Dialog>
                    )}
                    <Button
                        variant="ghost"
                        className="justify-start gap-2"
                        onClick={handleLogout}
                    >
                        <LogOut size={24} />
                        <span>{t('userMenu.logout')}</span>
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
